import React, { useState, useEffect } from 'react';
import { Search, Filter, ArrowUpDown } from 'lucide-react';
import { contactService } from '../services/api';
import ContactCard from '../componets/ContactCard';
import { useNavigate } from 'react-router-dom'; // Add this import

const Contacts = () => { 
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('importance'); // 'recent', 'importance', 'name'
  const navigate = useNavigate(); // Add this hook

  const styles = {
    page: {
      padding: '2rem',
    },
    header: {
      marginBottom: '2rem',
    },
    title: {
      fontSize: '2rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 800,
      color: '#0f172a',
      marginBottom: '0.5rem',
    },
    subtitle: {
      color: '#475569',
      fontSize: '1rem',
    },
    controls: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'center',
      marginBottom: '1rem',
      flexWrap: 'nowrap', // Change from 'wrap' to 'nowrap'
    },

    searchBox: {
      flex: 1,
      minWidth: '250px', // Reduce from 300px to give more space
      position: 'relative',
    },
    searchInput: {
      width: '80%',
      padding: '0.875rem 1rem 0.875rem 3rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '1rem',
      transition: 'all 200ms',
      fontFamily: 'inherit',
      background: '#ffffff',
      outline: 'none',
    },
    searchIcon: {
      position: 'absolute',
      left: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#94a3b8',
    },
    sortContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      flexShrink: 0, // Add this - prevents sort from shrinking
    },
    sortLabel: {
      fontSize: '0.9rem',
      color: '#475569',
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    sortSelect: {
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 600,
      background: '#ffffff',
      color: '#0f172a',
      cursor: 'pointer',
      outline: 'none',
      transition: 'all 200ms',
    },
      legend: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem', // Reduce from 1.5rem
        padding: '0.75rem 1rem', // Reduce horizontal padding
        background: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        flexShrink: 0, // Add this - prevents legend from shrinking
        whiteSpace: 'nowrap', // Add this - prevents text wrapping
      },
    legendTitle: {
      fontSize: '0.85rem',
      fontWeight: 600,
      color: '#475569',
      marginRight: '0.5rem',
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.85rem',
      color: '#475569',
    },
    legendDot: {
      width: '16px',
      height: '16px',
      borderRadius: '4px',
      border: '1px solid #e2e8f0',
    },
    legendGradient: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    gradientBar: {
      width: '120px',
      height: '16px',
      borderRadius: '4px',
      background: 'linear-gradient(to right, rgba(34, 197, 94, 0.2), rgba(239, 68, 68, 0.2))',
      border: '1px solid #e2e8f0',
    },
    gradientLabel: {
      fontSize: '0.75rem',
      color: '#64748b',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: '1.5rem',
    },
    empty: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#475569',
    },
    emptyTitle: {
      fontSize: '1.5rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      marginBottom: '0.5rem',
      color: '#0f172a',
    },
    emptyText: {
      fontSize: '1rem',
      marginBottom: '1.5rem',
    },
    button: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.875rem 1.5rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
    },
    loading: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#475569',
      fontSize: '1.1rem',
    },
  };

const getRecencyColor = (contact) => {
  // Get the most recent date between last_contacted and created_at
  const lastContactedDate = contact.last_contacted ? new Date(contact.last_contacted) : null;
  const createdDate = contact.created_at ? new Date(contact.created_at) : null;
  
  // Use whichever is most recent
  const mostRecentDate = lastContactedDate && createdDate 
    ? (lastContactedDate > createdDate ? lastContactedDate : createdDate)
    : (lastContactedDate || createdDate);
  
  // If no date available, return neutral color
  if (!mostRecentDate) {
    return 'rgba(203, 213, 225, 0.15)'; // light gray
  }
  
  const now = new Date();
  const daysSinceContact = Math.floor((now - mostRecentDate) / (1000 * 60 * 60 * 24));
  
  // Find the min and max days across ALL contacts
  const allDays = contacts.map(c => {
    const lastDate = c.last_contacted ? new Date(c.last_contacted) : null;
    const createDate = c.created_at ? new Date(c.created_at) : null;
    const relevantDate = lastDate && createDate 
      ? (lastDate > createDate ? lastDate : createDate)
      : (lastDate || createDate);
    
    if (!relevantDate) return null;
    return Math.floor((now - relevantDate) / (1000 * 60 * 60 * 24));
  }).filter(d => d !== null);
  
  const minDays = Math.min(...allDays); // Most recent contact
  const maxDays = Math.max(...allDays); // Oldest contact
  
  // Prevent division by zero if all contacts have same date
  const range = maxDays - minDays || 1;
  
  // Calculate ratio (0 = green = most recent, 1 = red = oldest)
  const ratio = (daysSinceContact - minDays) / range;
  
  // Interpolate between green and red
  // Green: rgb(34, 197, 94) -> Red: rgb(239, 68, 68)
  const r = Math.round(34 + (239 - 34) * ratio);
  const g = Math.round(197 + (68 - 197) * ratio);
  const b = Math.round(94 + (68 - 94) * ratio);
  
  // Return with low opacity for subtle tint
  return `rgba(${r}, ${g}, ${b}, 0.12)`;
};
  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const data = await contactService.getContacts();
      setContacts(data.contacts);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImportanceValue = (importance) => {
    const values = {
      critical: 4,
      high: 3,
      medium: 2,
      low: 1,
    };
    // Handle undefined, null, or empty string
    if (!importance) return 0;
    return values[importance.toLowerCase()] || 0;
  };

  const sortContacts = (contactsList) => {
    const sorted = [...contactsList];
    
    switch (sortBy) {
      case 'importance':
        return sorted.sort((a, b) => {
          const aValue = getImportanceValue(a.contact_importance);
          const bValue = getImportanceValue(b.contact_importance);
          console.log(`Sorting: ${a.name} (${a.contact_importance || 'none'}=${aValue}) vs ${b.name} (${b.contact_importance || 'none'}=${bValue})`);
          return bValue - aValue; // Highest importance first
        });
      
      case 'recent':
        return sorted.sort((a, b) => {
          const aDate = a.last_contacted ? new Date(a.last_contacted) : new Date(0);
          const bDate = b.last_contacted ? new Date(b.last_contacted) : new Date(0);
          return bDate - aDate; // Most recent first
        });
      
      case 'name':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      
      default:
        return sorted;
    }
  };

  const filteredContacts = contacts.filter((contact) =>
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedAndFilteredContacts = sortContacts(filteredContacts);

  if (loading) {
    return <div style={styles.loading}>Loading your contacts...</div>;
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>My Contacts</h1>
        <p style={styles.subtitle}>
          Manage your network of {contacts.length} connections
        </p>
      </div>

      <div style={styles.controls}>
        <div style={styles.searchBox}>
          <Search size={20} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search contacts by name, company, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
            onFocus={(e) => {
              e.target.style.borderColor = '#2563eb';
              e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        {/* Color Legend - moved here */}
        <div style={styles.legend}>
          <span style={styles.legendTitle}>Contact Recency:</span>
          <div style={styles.legendGradient}>
            <div style={styles.legendItem}>
              <div style={{...styles.legendDot, background: 'rgba(34, 197, 94, 0.2)'}}></div>
              <span style={styles.gradientLabel}>Most Recent</span>
            </div>
            <div style={styles.gradientBar}></div>
            <div style={styles.legendItem}>
              <div style={{...styles.legendDot, background: 'rgba(239, 68, 68, 0.2)'}}></div>
              <span style={styles.gradientLabel}>Least Recent</span>
            </div>
          </div>
        </div>

        <div style={styles.sortContainer}>
          <label style={styles.sortLabel}>
            <ArrowUpDown size={18} />
            Sort by:
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={styles.sortSelect}
            onFocus={(e) => {
              e.target.style.borderColor = '#2563eb';
              e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.boxShadow = 'none';
            }}
          >
            {/* <option value="recent">Last Contacted</option> */}
            <option value="importance">Importance</option>
            <option value="name">Name (A-Z)</option>
          </select>
        </div>
      </div>

      {/* Color Legend */}
      {/* <div style={styles.legend}>
        <span style={styles.legendTitle}>Contact Recency:</span>
        <div style={styles.legendGradient}>
          <div style={styles.legendItem}>
            <div style={{...styles.legendDot, background: 'rgba(34, 197, 94, 0.2)'}}></div>
            <span style={styles.gradientLabel}>Most Recent</span>
          </div>
          <div style={styles.gradientBar}></div>
          <div style={styles.legendItem}>
            <div style={{...styles.legendDot, background: 'rgba(239, 68, 68, 0.2)'}}></div>
            <span style={styles.gradientLabel}>Least Recent</span>
          </div>
        </div>
      </div> */}

      {sortedAndFilteredContacts.length === 0 ? (
        <div style={styles.empty}>
          <h2 style={styles.emptyTitle}>
            {searchTerm ? 'No contacts found' : 'No contacts yet'}
          </h2>
          <p style={styles.emptyText}>
            {searchTerm
              ? 'Try adjusting your search terms'
              : 'Start building your network by adding your first contact'}
          </p>
        </div>
      ) : (
         <div style={styles.grid}>
          {sortedAndFilteredContacts.map((contact) => (
            <ContactCard
              key={contact.id}
              contact={contact}
              onClick={() => navigate(`/dashboard/contacts/${contact.id}`)} // Change this line
              backgroundColor={getRecencyColor(contact)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Contacts;